import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Send, CheckCircle2, Upload, Loader2 } from 'lucide-react';
import { agencyAPI } from '../../lib/api';

export const SendCVButton = ({ agency }) => {
    const navigate = useNavigate();
    const [status, setStatus] = useState(null);
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);

    useEffect(() => {
        agencyAPI.cvStatus(agency.agency_id).then(r => setStatus(r.data)).catch(() => setStatus({ sent: false, has_cv: false }));
    }, [agency.agency_id]);

    if (!status) return null;
    if (!agency.email || !agency.email.includes('@')) return null;

    if (status.sent) {
        return (
            <Button disabled className="w-full bg-emerald-600 disabled:opacity-100" data-testid="cv-sent-badge">
                <CheckCircle2 className="w-4 h-4 mr-2" /> CV sent to this agency
            </Button>
        );
    }
    if (!status.has_cv) {
        return (
            <Button variant="outline" className="w-full" onClick={() => navigate('/settings')} data-testid="upload-cv-to-send-btn">
                <Upload className="w-4 h-4 mr-2" /> Upload your CV to send it
            </Button>
        );
    }

    const send = async () => {
        setSending(true);
        try {
            await agencyAPI.submitCV(agency.agency_id, { agency_id: agency.agency_id, message });
            setStatus({ ...status, sent: true });
            setOpen(false);
            toast.success(`Your CV has been emailed to ${agency.name}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not send your CV');
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            <Button className="w-full bg-primary-600 hover:bg-primary-700" onClick={() => setOpen(true)} data-testid="send-cv-btn">
                <Send className="w-4 h-4 mr-2" /> Send my CV
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent data-testid="send-cv-dialog">
                    <DialogHeader>
                        <DialogTitle>Send your CV to {agency.name}</DialogTitle>
                        <DialogDescription>We'll email your saved CV to the agency. They can reply to you directly. You can only send it once.</DialogDescription>
                    </DialogHeader>
                    <Textarea
                        placeholder="Optional note, e.g. the roles or locations you're looking for"
                        value={message}
                        maxLength={2000}
                        onChange={(e) => setMessage(e.target.value)}
                        data-testid="send-cv-message"
                    />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)} data-testid="send-cv-cancel">Cancel</Button>
                        <Button onClick={send} disabled={sending} data-testid="send-cv-confirm">
                            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                            Send CV
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};
